import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Lock, Loader2, FileText, X } from 'lucide-react';
import { InviteService, LegalDocumentService, LegalDocumentDTO } from '../services/apiService';

export const AccettazioneInvitoView: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [step, setStep] = useState<'VALIDATING' | 'FORM' | 'SUCCESS'>('VALIDATING');
    const [email, setEmail] = useState('');

    // Anagrafica + credenziali
    const [nome, setNome] = useState('');
    const [cognome, setCognome] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPass, setConfirmPass] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Consensi ai documenti legali: `docs` è l'elenco (obbligatori e facoltativi),
    // `checked` mappa id → spuntato, `openDocId` è il documento aperto nella modale.
    const [docs, setDocs] = useState<LegalDocumentDTO[]>([]);
    const [checked, setChecked] = useState<Record<number, boolean>>({});
    const [openDocId, setOpenDocId] = useState<number | null>(null);

    // "Ho letto, accetto" si abilita solo dopo aver scorso il testo fino in fondo,
    // come prova che il documento sia stato letto e non solo aperto.
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [reachedBottom, setReachedBottom] = useState(false);

    // A ogni nuovo documento la guardia si resetta; se il testo non scorre si abilita subito.
    useEffect(() => {
        if (openDocId === null) return;
        setReachedBottom(false);
        requestAnimationFrame(() => {
            const el = contentRef.current;
            if (el && el.scrollHeight <= el.clientHeight + 4) {
                setReachedBottom(true);
            }
        });
    }, [openDocId]);

    const handleContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        // Tolleranza di 24px per non pretendere il pixel esatto.
        if (el.scrollHeight - el.scrollTop - el.clientHeight <= 24) {
            setReachedBottom(true);
        }
    };

    useEffect(() => {
        if (!token) {
            setError("Token mancante o non valido.");
            return;
        }
        // Valida il token e intanto scarica i documenti da accettare per il ruolo invitato.
        Promise.all([
            InviteService.validateToken(token),
            LegalDocumentService.getForInvite(token).catch(() => [] as LegalDocumentDTO[])
        ])
            .then(([validation, legalDocs]) => {
                if (!validation.valid) {
                    setError("Questo invito è scaduto, già usato o non valido.");
                    return;
                }
                setDocs(legalDocs);
                setStep('FORM');
                // Dati inseriti dal titolare: l'invitato li controlla e li corregge se serve.
                InviteService.getInviteData(token)
                    .then(d => {
                        if (d.email) setEmail(d.email);
                        if (d.nome) setNome(n => n || d.nome!);
                        if (d.cognome) setCognome(c => c || d.cognome!);
                    })
                    .catch(() => { /* il modulo resta da compilare a mano */ });
            })
            .catch(() => setError("Questo invito è scaduto o non valido."));
    }, [token]);

    // Documento aperto nella modale full-screen di lettura.
    const openDoc = useMemo(
        () => docs.find(d => d.id === openDocId) || null,
        [openDocId, docs]
    );

    // Tutti i documenti obbligatori sono stati spuntati?
    const allMandatoryChecked = useMemo(() =>
        docs.filter(d => d.mandatory).every(d => checked[d.id]),
        [docs, checked]
    );

    // La checkbox non si spunta direttamente: solo tramite "Ho letto, accetto"
    // nella modale, così l'utente deve aver aperto il testo.

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (password !== confirmPass) { setError("Le password non coincidono"); return; }
        if (password.length < 8) { setError("La password deve essere di almeno 8 caratteri"); return; }
        if (!allMandatoryChecked) { setError("Devi accettare tutti i documenti obbligatori"); return; }

        // Inviamo tutti i documenti spuntati; il BE registra un UserConsent per
        // ciascuno con data, IP e user agent.
        const acceptances = docs
            .filter(d => checked[d.id])
            .map(d => ({
                documentId: d.id,
                documentType: d.type,
                version: d.version,
            }));

        setSubmitting(true);
        try {
            await InviteService.completeRegistration(token!, password, nome, cognome, acceptances);
            setStep('SUCCESS');
        } catch (err: any) {
            const msg = err?.response?.data?.message
                || "Errore durante l'attivazione. Ricarica la pagina e riprova.";
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (error && step === 'VALIDATING') {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 font-sans">
                <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
                    <div className="text-red-500 font-bold text-xl mb-2">Link non valido</div>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    if (step === 'VALIDATING') {
        return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><Loader2 className="animate-spin text-blue-600" size={40}/></div>;
    }

    if (step === 'SUCCESS') {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 font-sans">
                <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600">
                        <CheckCircle size={32}/>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Account attivato</h2>
                    <p className="text-gray-600 mb-6">
                        Benvenuto, <strong>{nome}</strong>. Il tuo account è stato configurato correttamente e i tuoi consensi sono stati registrati.
                    </p>
                    <button onClick={() => navigate('/login')} className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200">
                        Vai al Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 font-sans">
            <div className="bg-white p-8 rounded-2xl shadow-xl max-w-lg w-full border border-gray-200">
                <div className="text-center mb-6">
                    <img src="/logo192.png" alt="Ultra Detective" className="w-14 h-auto mx-auto mb-2 object-contain" />
                    <p className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-4">Ultra Detective</p>
                    <h1 className="text-2xl font-bold text-gray-900">Completa la registrazione</h1>
                    <p className="text-gray-500 text-sm mt-2">
                        Imposta le tue credenziali e accetta i documenti richiesti.
                    </p>
                    {email && (
                        <p className="text-xs text-gray-400 mt-1">{email}</p>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Nome</label>
                            <input required type="text" className="w-full p-3 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none" value={nome} onChange={e => setNome(e.target.value)}/>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Cognome</label>
                            <input required type="text" className="w-full p-3 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none" value={cognome} onChange={e => setCognome(e.target.value)}/>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Crea password</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input required type="password" placeholder="Almeno 8 caratteri" className="w-full pl-10 p-3 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500" value={password} onChange={e => setPassword(e.target.value)}/>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Conferma password</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input required type="password" placeholder="Ripeti password" className="w-full pl-10 p-3 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500" value={confirmPass} onChange={e => setConfirmPass(e.target.value)}/>
                        </div>
                    </div>

                    {/* Documenti legali da accettare */}
                    {docs.length > 0 && (
                        <div className="pt-4 border-t border-gray-200">
                            <label className="block text-xs font-bold text-gray-500 mb-3 uppercase">
                                Documenti da accettare
                            </label>
                            <ul className="space-y-3">
                                {docs.map(doc => (
                                    <li key={doc.id} className="flex items-start gap-3">
                                        <input
                                            id={`consent-${doc.id}`}
                                            type="checkbox"
                                            required={doc.mandatory}
                                            checked={!!checked[doc.id]}
                                            readOnly
                                            aria-readonly="true"
                                            onClick={(e) => {
                                                // Niente toggle diretto: si apre la modale.
                                                e.preventDefault();
                                                if (!checked[doc.id]) setOpenDocId(doc.id);
                                            }}
                                            className="mt-1 w-4 h-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                        />
                                        <span className="text-sm text-gray-700 flex-1 leading-relaxed">
                                            Ho letto e accetto{' '}
                                            <button
                                                type="button"
                                                onClick={() => setOpenDocId(doc.id)}
                                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 underline font-medium"
                                            >
                                                <FileText size={14} />
                                                {doc.title}
                                            </button>
                                            {doc.mandatory
                                                ? <span className="text-red-500 ml-1" aria-label="obbligatorio">*</span>
                                                : <span className="text-gray-400 ml-1 text-xs">(facoltativo)</span>}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <p className="text-xs text-gray-500 mt-3">
                                Per accettare devi aprire ciascun documento e confermare con
                                "Ho letto, accetto" in fondo alla finestra.
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                                Con la conferma verranno registrati data, ora, indirizzo IP e user-agent
                                come prova dell'avvenuta accettazione (art. 7 GDPR).
                            </p>
                        </div>
                    )}

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting || !allMandatoryChecked}
                        className="w-full bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white p-3 rounded-xl font-bold hover:bg-blue-700 transition-colors mt-6 shadow-lg shadow-blue-200 flex items-center justify-center gap-2"
                    >
                        {submitting && <Loader2 className="animate-spin" size={18}/>}
                        Attiva account
                    </button>
                </form>
            </div>

            {/* Modale lettura documento full-screen */}
            {openDoc && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch sm:items-center justify-center p-0 sm:p-6">
                    <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl shadow-2xl flex flex-col max-h-screen sm:max-h-[90vh]">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white sm:rounded-t-2xl">
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">{openDoc.title}</h2>
                                <p className="text-xs text-gray-500 mt-0.5">Versione {openDoc.version}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setOpenDocId(null)}
                                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                                aria-label="Chiudi"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div
                            ref={contentRef}
                            onScroll={handleContentScroll}
                            // Tipografia con arbitrary variants Tailwind per non dipendere da
                            // @tailwindcss/typography. Il contenuto è già sanificato dal BE alla pubblicazione.
                            className="p-6 overflow-y-auto flex-1 text-gray-800 text-sm leading-relaxed
                                [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:text-gray-900
                                [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:text-gray-900
                                [&_h3]:text-base [&_h3]:font-bold [&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-gray-900
                                [&_p]:mb-3
                                [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
                                [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
                                [&_li]:mb-1
                                [&_strong]:font-bold [&_em]:italic
                                [&_hr]:my-4 [&_hr]:border-gray-300
                                [&_a]:text-blue-600 [&_a]:underline
                                [&_table]:w-full [&_table]:my-3 [&_table]:text-xs
                                [&_th]:font-bold [&_th]:border-b [&_th]:border-gray-300 [&_th]:p-2 [&_th]:text-left
                                [&_td]:border-b [&_td]:border-gray-200 [&_td]:p-2 [&_td]:align-top
                                [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-3"
                            dangerouslySetInnerHTML={{ __html: openDoc.contentHtml }}
                        />
                        <div className="p-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row gap-3 sm:justify-between sm:items-center bg-white sm:rounded-b-2xl">
                            {!reachedBottom && (
                                <p className="text-xs text-amber-600 sm:mr-auto">
                                    Scorri il documento fino in fondo per poterlo accettare.
                                </p>
                            )}
                            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:ml-auto">
                                <button
                                    type="button"
                                    onClick={() => setOpenDocId(null)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Chiudi
                                </button>
                                <button
                                    type="button"
                                    disabled={!reachedBottom}
                                    onClick={() => { setChecked(prev => ({ ...prev, [openDoc.id]: true })); setOpenDocId(null); }}
                                    className="px-4 py-2 bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-bold hover:bg-blue-700"
                                >
                                    Ho letto, accetto
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccettazioneInvitoView;
